import { useState } from 'react';

import styled from 'styled-components';

import { Badge, BadgeVariant, BadgeSize } from '@components/badge';
import { theme } from '@styles/theme';

type TagSelectorProps = {
  variant?: BadgeVariant;
  size?: BadgeSize;
  options: Array<string>;
  selectedVariant?: BadgeVariant;
  selectedTags?: Array<string>;
  onClick: (tag: string) => void;
};

const TagSelector = ({
  options,
  variant = 'neutral',
  selectedVariant = 'blue',
  onClick,
  selectedTags = [],
  size = 'md',
}: TagSelectorProps) => {
  return (
    <TagSelectorContainer>
      {options.map((option) => {
        const isSelected = selectedTags.includes(option);
        return (
          <Badge
            key={option}
            size={size}
            variant={isSelected ? selectedVariant : variant}
            isInteractive
            onClick={() => onClick(option)}
            text={option}
          />
        );
      })}
    </TagSelectorContainer>
  );
};

export type TagSelectorMultiProps = Omit<TagSelectorProps, 'onClick'> & {
  defaultValue?: Array<string>;
  onChange: (tags: Array<string>) => void;
};

export const TagSelectorMulti = ({
  onChange,
  defaultValue = [],
  ...rest
}: TagSelectorMultiProps) => {
  const [selectedTags, setSelectedTags] = useState<string[]>(defaultValue);

  const handleTagClick = (option: string) => {
    const newTags = selectedTags.includes(option)
      ? selectedTags.filter((tag) => tag !== option)
      : [...selectedTags, option];
    setSelectedTags(newTags);
    onChange(newTags);
  };

  return <TagSelector {...rest} selectedTags={selectedTags} onClick={handleTagClick} />;
};

export type TagSelectorSingleProps = Omit<TagSelectorProps, 'onClick'> & {
  defaultValue?: string;
  onChange: (tag: string) => void;
};

export const TagSelectorSingle = ({
  defaultValue = '',
  onChange,
  ...rest
}: TagSelectorSingleProps) => {
  const [selected, setSelected] = useState(defaultValue);

  const handleTagClick = (option: string) => {
    setSelected(option);
    onChange(option);
  };

  return (
    <TagSelector {...rest} selectedTags={selected ? [selected] : []} onClick={handleTagClick} />
  );
};

const TagSelectorContainer = styled.div({
  display: 'flex',
  flexWrap: 'wrap',
  justifyContent: 'center',
  gap: theme.space[2],
});
